<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountingEntries" />
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }" v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom title="Nuevo asiento manual" aria-label="Nuevo asiento manual" @click="openManualAccounting()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width:170px;"><label class="filter-label">Referencia</label><b-form-input type="text" size="sm" v-model="filters.reference" placeholder="Buscar..." @keyup.enter="filterReportReset()"></b-form-input></div>
            <div class="list-view-filter" style="width:170px;"><label class="filter-label">Empresa</label><b-form-select size="sm" v-model="filters.business_id" :options="arr.businesses" @change="onBusinessChange()"></b-form-select></div>
            <div class="list-view-filter" style="width:110px;"><label class="filter-label">Tipo</label><b-form-select size="sm" v-model="filters.declared" :options="arr.select.declared" @change="onFilterDeclaredChange()"></b-form-select></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Desde</label><b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Hasta</label><b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input></div>
            <div class="list-view-filter" style="width:120px;"><label class="filter-label">Estado</label><b-form-select size="sm" v-model="filters.status" :options="arr.select.status"></b-form-select></div>
            <div class="list-view-filter" style="width:120px;"><label class="filter-label">Origen</label><b-form-select size="sm" v-model="filters.origin" :options="arr.select.origin"></b-form-select></div>
            <div class="list-view-filter" style="width:190px;"><label class="filter-label">Movimiento</label><b-form-select size="sm" v-model="filters.source_type" :options="arr.select.sourceType"></b-form-select></div>
            <div class="list-view-filter" style="width:200px;"><label class="filter-label">Cuenta contable</label><b-form-select size="sm" v-model="filters.accounting_accounts_id" :options="arr.leafAccounts"></b-form-select></div>
            <b-button variant="outline-dark" size="sm" @click="filterReportReset()" v-b-tooltip.hover title="Aplicar filtros" style="height:31px;width:31px;padding:0;flex-shrink:0;"><AppIcon name="filter" /></b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row class="mt-3">
          <b-col>
            <b-table class="app-table mb-0"
                    sticky-header="60vh"
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :fields="table.fields"
                    :filter="table.filter"
                    :current-page="table.currentPage"
                    :per-page="table.perPage"
                    :items="table.items"
                    :tbody-tr-class="entryRowClass"
                    @row-clicked="openDetail"
                    v-if="table.items.length">

              <template v-slot:cell(date)="data">
                <div v-if="data.item.date">
                  {{ moment(data.item.date).format('DD MMMM YYYY') }}
                </div>
              </template>

              <template v-slot:cell(nd_marker)="data">
                <span v-if="data.item.declared != null && !data.item.declared" class="nd-marker" v-b-tooltip.hover title="ND"></span>
              </template>

              <template v-slot:cell(reference)="data">
                {{data.item.reference}}
              </template>

              <template v-slot:cell(observations)="data">
                {{data.item.observations}}
              </template>

              <template v-slot:cell(origin)="data">
                <b-badge :variant="originVariant(data.item.origin)" style="font-size:12px; padding:4px 8px;">
                  {{ originLabel(data.item.origin) }}
                </b-badge>
              </template>

              <template v-slot:cell(total_debit)="data">
                <b>{{ formatCurrency(data.item.total_debit) }}</b>
              </template>

              <template v-slot:cell(total_credit)="data">
                <b>{{ formatCurrency(data.item.total_credit) }}</b>
                <b-badge v-if="!data.item.is_balanced" variant="danger" class="d-block mt-1">Diferencia de {{ formatCurrency(Math.abs(data.item.difference)) }}</b-badge>
              </template>

              <template v-slot:cell(f_action)="data">
                <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle">
                  <template #button-content><AppIcon name="more_vert" label="Acciones" /></template>
                  <b-dropdown-item @click="openDetail(data.item)"><AppIcon name="visibility" /> Ver detalle</b-dropdown-item>
                  <template v-if="['manual', 'opening'].includes(data.item.origin) && !isPeriodLocked(data.item.date)">
                    <b-dropdown-item @click="openManualAccounting(data.item)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                    <b-dropdown-item @click="deleteManualAccounting(data.item)"><AppIcon name="delete" /> Eliminar</b-dropdown-item>
                  </template>
                  <b-dropdown-item v-if="data.item.origin == 'automatic' && data.item.total_debit > 0 && !isPeriodLocked(data.item.date)" @click="revertApproved(data.item)"><AppIcon name="undo" /> Borrar asiento</b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
            <EmptyState v-else icon="receipt_long" title="Sin asientos" description="No se encontraron asientos con estos filtros." />
            <div class="app-table-footer" v-if="table.tablePaginate">
              <span class="list-view-toolbar__count">{{ table.tablePaginate.total }} registros</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
          <nav v-if="table.tablePaginate">
            <b-pagination class="mb-0" size="sm" pills="" :total-rows="table.tablePaginate.total" :per-page="table.tablePaginate.per_page" v-model="table.currentPage" @input="filterReport()" />
          </nav>
        </div>
      </b-card>

      <b-modal v-model="modal.manual.active"
               size="xl"
               :key="'manual_' + modal.manual._id"
               :id="modal.manual._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit_note" /></span>
          <h5 class="app-modal-header__title">{{ modal.manual.title }}</h5>
        </div>

        <b-row>
          <b-col md="2">
            <b-form-group label="Empresa *">
              <b-form-select size="sm" v-model="crud.manual.business_id" :options="arr.businesses"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Tipo *">
              <b-form-select size="sm" v-model="crud.manual.declared" :options="arr.select.declaredRequired"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-form-group label="Fecha">
              <b-form-input type="date" size="sm" v-model="crud.manual.date"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Referencia">
              <b-form-input type="text" size="sm" v-model="crud.manual.reference" placeholder="Ej: AJUSTE-0001"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Observaciones">
              <app-textarea size="sm" v-model="crud.manual.observations" rows="2" max-rows="6"></app-textarea>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <AccountingEntryBuilder
              :key="'manual_accounting_' + manualAccountingKey"
              typeDebe=""
              typeHaber=""
              :defaultDebe="crud.manual.defaultDebe"
              :defaultHaber="crud.manual.defaultHaber"
              :reference="crud.manual.reference"
              :amountTotal="0"
              validationMode="manual"
              :showConstructor="true"
              :editableAmounts="crud.manual.origin == 'opening'"
              @getAsiento="loadManualAccountingDetail($event)" />
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.manual.active=false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" @click="saveManualAccounting()"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </div>
      </b-modal>

      <b-modal v-model="modal.detail.active"
               size="lg"
               :key="'detail_' + modal.detail._id"
               :id="modal.detail._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{ modal.detail.title }}</h5>
        </div>

        <div v-if="selectedAccounting">
          <p class="small text-muted mb-1" v-if="selectedAccounting.approved_at">
            Aprobado el {{ moment(selectedAccounting.approved_at).format('DD/MM/YYYY HH:mm') }}<span v-if="selectedAccounting.approved_user_name"> por {{ selectedAccounting.approved_user_name }}</span><span v-if="selectedAccounting.suggestion_edited">, con cambios sobre la sugerencia</span>.
          </p>
          <div class="d-flex flex-wrap mb-3 px-3 py-2 rounded border" style="background:var(--app-color-surface-subtle);">
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Fecha</small>
              <span>{{ moment(selectedAccounting.date).format('DD/MM/YYYY') }}</span>
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Referencia</small>
              <span>{{ selectedAccounting.reference }}</span>
            </div>
            <div class="mr-4">
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Origen</small>
              <b-badge :variant="selectedAccounting.origin == 'manual' ? 'info' : 'secondary'" style="font-size:12px; padding:3px 8px;">
                {{ selectedAccounting.origin_movement ? selectedAccounting.origin_movement.label : selectedAccounting.origin }}
              </b-badge>
            </div>
            <div>
              <small class="d-block text-muted text-uppercase font-weight-bold" style="font-size:10px;">Estado</small>
              <b-badge :variant="selectedAccounting.is_balanced ? 'success' : 'danger'" style="font-size:12px; padding:3px 8px;">
                {{ selectedAccounting.is_balanced ? 'Balanceado' : 'Con diferencia' }}
              </b-badge>
            </div>
          </div>

          <AccountingEntrySource v-if="selectedAccounting.source_type" :accounting-id="selectedAccounting.id" />

          <b-table class="app-table app-table--compact mb-0"
                   responsive="sm"
                   :hover="true"
                   :small="true"
                   :items="tableSub.items"
                   :fields="tableSub.fields"
                   :tbody-tr-class="subTableRowClass"
                   v-if="tableSub.items.length">
            <template v-slot:cell(code)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{data.item.accounting_accounts_code}}
              </span>
            </template>
            <template v-slot:cell(name)="data">
              <span :style="data.item.type === 'Haber' ? 'padding-left:2rem;' : ''">
                {{data.item.accounting_accounts_name}}
              </span>
            </template>
            <template v-slot:cell(debit)="data">
              <b v-if="data.item.type == 'Debe'" class="accounting-entries-amount">{{ formatCurrency(data.item.total) }}</b>
            </template>
            <template v-slot:cell(credit)="data">
              <b v-if="data.item.type == 'Haber'" class="accounting-entries-amount">{{ formatCurrency(data.item.total) }}</b>
            </template>
          </b-table>
          <EmptyState v-else icon="receipt_long" title="Sin renglones" description="Este asiento todavía no tiene renglones." />

          <div class="d-flex justify-content-end mt-3">
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:var(--app-color-warning-soft);">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Debe</small>
              <strong style="font-size:15px;">{{ formatCurrency(selectedAccounting.total_debit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2 mr-2" style="min-width:150px; background:var(--app-color-info-soft);">
              <small class="d-block text-muted font-weight-bold text-uppercase" style="font-size:10px;">Haber</small>
              <strong style="font-size:15px;">{{ formatCurrency(selectedAccounting.total_credit) }}</strong>
            </div>
            <div class="text-center border rounded px-3 py-2" style="min-width:150px;"
                 :style="{ background: selectedAccounting.is_balanced ? 'var(--app-color-success-soft)' : 'var(--app-color-danger-soft)' }">
              <small class="d-block font-weight-bold text-uppercase" style="font-size:10px;"
                     :class="selectedAccounting.is_balanced ? 'text-success' : 'text-danger'">Diferencia</small>
              <strong style="font-size:15px;"
                      :class="selectedAccounting.is_balanced ? 'text-success' : 'text-danger'">{{ formatCurrency(selectedAccounting.difference) }}</strong>
            </div>
          </div>
        </div>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" @click="modal.detail.active=false"><AppIcon name="close" class="mr-1" />Cerrar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>
<script>
  import AccountingHelp from './inc/AccountingHelp'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import StorageLocal from '@/handler/storageLocal'
  import AccountingEntrySource from './inc/AccountingEntrySource'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import moment from 'moment'
  import FindObject from '@/components/inc/find/findObject'
  import AccountingEntryBuilder from '@/components/inc/accounting/entries'

  export default {
    components: {
      AccountingEntrySource,
      AccountingHelp,
      TabHeaderActions,
      FindObject,
      AccountingEntryBuilder,
    },
    data: () => {
      return {
        filtersOpen: false,
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingEntries',
          elements: {}
        },
        table : {
          items: [],
          filters: null,
          fields: [
            {key: 'date', label: 'Fecha', class: "align-middle"},
            {key: 'nd_marker', label: '', class: "align-middle text-center p-0", thStyle: 'width:24px'},
            {key: 'reference', label: 'Referencia', class: "align-middle"},
            {key: 'observations', label: 'Observaciones', class: "align-middle"},
            {key: 'origin', label: 'Origen', class: "align-middle text-center"},
            {key: 'total_debit', label: 'Debe', class: "align-middle text-right"},
            {key: 'total_credit', label: 'Haber', class: "align-middle text-right"},
            {key: 'f_action', label:'', class: "align-middle"},
          ],
          currentPage: 1,
          perPage: 0,
          tablePaginate: null,
        },
        tableSub : {
          items: [],
          fields: [
            {key: 'code', label: 'Código', class:'align-middle'},
            {key: 'name', label: 'Nombre', class:'align-middle'},
            {key: 'debit', label: 'Debe', class:'align-middle text-right'},
            {key: 'credit', label: 'Haber', class:'align-middle text-right'},
          ],
          filter: null,
          currentPage: 1,
          perPage: 50,
        },
        arr: {
          accounting: [],
          businesses: [],
          leafAccounts: [],
          select: {
            status: [
              { value: '', text: 'Todos' },
              { value: 'balanced', text: 'Balanceado' },
              { value: 'unbalanced', text: 'Con dif.' },
            ],
            sourceType: [
              { value: '', text: 'Todos' },
              { value: 'sales', text: 'Ventas' },
              { value: 'purchase', text: 'Compras' },
              { value: 'receipts', text: 'Recibos' },
              { value: 'payments', text: 'Pagos' },
              { value: 'funds', text: 'Fondos' },
              { value: 'cheque_deposits', text: 'Depósito de cheque' },
              { value: 'cheque_credits', text: 'Acreditación de cheque' },
              { value: 'cheque_rejections', text: 'Cheque rechazado' },
              { value: 'cheque_returns', text: 'Cheque vuelto a cartera' },
              { value: 'cheque_resolutions', text: 'Rechazo resuelto' },
            ],
            origin: [
              { value: '', text: 'Todos' },
              { value: 'automatic', text: 'Auto' },
              { value: 'manual', text: 'Manual' },
              { value: 'closing', text: 'Cierre' },
              { value: 'opening', text: 'Apertura' },
            ],
            declared: [
              { value: '', text: 'Todos' },
              { value: '1', text: 'D' },
              { value: '0', text: 'ND' },
            ],
            declaredRequired: [
              { value: null, text: 'Seleccionar tipo...' },
              { value: true, text: 'Declarado (D)' },
              { value: false, text: 'No Declarado (ND)' },
            ],
          }
        },
        filters: {
          date_start: '',
          date_end: '',
          reference: '',
          accounting_accounts_id: 0,
          status: '',
          origin: '',
          source_type: '',
          business_id: 0,
          declared: '',
        },
        crud: {
          manual: {
            id: 0,
            date: '',
            reference: '',
            observations: '',
            origin: 'manual',
            detail: null,
            defaultDebe: [],
            defaultHaber: [],
            business_id: 0,
            business_obj: null,
            declared: null,
          }
        },
        modal: {
          manual: {
            active: false,
            title: 'Nuevo asiento manual',
            _id: '',
          },
          detail: {
            active: false,
            title: 'Detalle de asiento',
            _id: '',
          }
        },
        manualAccountingKey: 0,
        selectedAccounting: null,
        maxClosingDate: null,
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_accounting_entries', value ? 'true' : 'false')
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_accounting_entries') == 'true'
      this.filterLoad()
      this.loadBusinesses()
      this.loadLeafAccounts()
    },
    methods: {
      moment,
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
        .then(response => {
          const businesses = response.data || []
          this.arr.businesses = businesses.map(b => ({ value: b.id, text: b.name }))
          if(businesses.length) {
            this.filters.business_id = businesses[0].id
          }
          this.loadClosingDates()
          this.filterReport()
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      loadLeafAccounts() {
        serviceAPI.filtrarPlanCuenta()
        .then(response => {
          const leaves = []
          this.collectLeafAccounts(response.data || [], leaves)
          this.arr.leafAccounts = [
            { value: 0, text: 'Todas las cuentas' },
            ...leaves.map(a => ({ value: a.id, text: a.code + ' - ' + a.name }))
          ]
        })
      },
      collectLeafAccounts(accounts, result) {
        accounts.forEach(account => {
          const children = account.children_recurrent || account.children || []
          if(children.length) {
            this.collectLeafAccounts(children, result)
          } else {
            result.push(account)
          }
        })
      },
      loadClosingDates() {
        const params = {}
        if(this.filters.business_id) params.business_id = this.filters.business_id
        if(this.filters.declared !== '') params.declared = this.filters.declared

        serviceAPI.obtenerFechasCierre(params)
        .then(response => {
          const dates = response.data || []
          this.maxClosingDate = dates.length ? dates[0] : null
        })
      },
      onBusinessChange() {
        this.loadClosingDates()
        this.filterReportReset()
      },
      onFilterDeclaredChange() {
        this.loadClosingDates()
      },
      loadManualBusiness(object) {
        if(object) {
          this.crud.manual.business_obj = object
          this.crud.manual.business_id = object.id
        } else {
          this.crud.manual.business_obj = null
          this.crud.manual.business_id = 0
        }
      },
      isPeriodLocked(date) {
        if(!this.maxClosingDate || !date) return false
        return date.substring(0, 10) <= this.maxClosingDate
      },
      entryRowClass(item) {
        if(!item) return ''
        return item.is_balanced ? 'accounting-entry-row' : 'accounting-entry-row table-danger'
      },
      subTableRowClass(item) {
        if (!item) return ''
        return item.type === 'Debe' ? 'table-warning' : 'table-info'
      },
      originVariant(origin) {
        const map = { manual: 'info', closing: 'dark', opening: 'success' }
        return map[origin] || 'secondary'
      },
      originLabel(origin) {
        const map = { manual: 'Manual', closing: 'Cierre', opening: 'Apertura' }
        return map[origin] || 'Automático'
      },
      openDetail(item) {
        this.selectedAccounting = item
        this.tableSub.items = item.details || []
        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = 'Detalle de asiento: ' + item.reference
        this.modal.detail.active = true
      },
      filterLoad() {
        this.filters.date_start = moment().startOf('month').format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
      },
      filterReportReset() {
        this.table.currentPage = 1
        this.tableSub.items = []
        this.filterReport()
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency: 'ARS'}).format(value || 0)
      },
      openManualAccounting(item) {
        this.crud.manual.id = item ? item.id : 0
        this.crud.manual.date = item ? item.date : moment().format('YYYY-MM-DD')
        this.crud.manual.reference = item ? item.reference : ''
        this.crud.manual.observations = item ? item.observations : ''
        this.crud.manual.origin = item ? item.origin : 'manual'
        this.crud.manual.detail = null
        this.crud.manual.defaultDebe = []
        this.crud.manual.defaultHaber = []
        this.crud.manual.business_id = item ? (item.business_id || 0) : (this.arr.businesses.length ? this.arr.businesses[0].value : 0)
        this.crud.manual.business_obj = null
        this.crud.manual.declared = item ? (item.declared !== null && item.declared !== undefined ? item.declared : null) : null

        if(item && item.details) {
          item.details.forEach(element => {
            let row = {
              account: {
                id: element.accounting_accounts_id,
                code: element.accounting_accounts_code,
                name: element.accounting_accounts_name,
              },
              amount: parseFloat(element.total),
            }

            if(element.type == 'Debe') {
              this.crud.manual.defaultDebe.push(row)
            }

            if(element.type == 'Haber') {
              this.crud.manual.defaultHaber.push(row)
            }
          })
        }

        this.manualAccountingKey = this.manualAccountingKey + 1

        this.modal.manual._id = Math.random().toString(36).slice(2, 8)
        this.modal.manual.title = item ? (item.origin == 'opening' ? 'Editar asiento de apertura' : 'Editar asiento manual') : 'Nuevo asiento manual'
        this.modal.manual.active = true
      },
      loadManualAccountingDetail(object) {
        this.crud.manual.detail = object
      },
      saveManualAccounting() {
        if(!this.crud.manual.business_id) {
          this.$awn.alert('Debe seleccionar la empresa')
          return false
        }

        if(this.crud.manual.declared === null || this.crud.manual.declared === undefined) {
          this.$awn.alert('Debe seleccionar el tipo (Declarado / No Declarado)')
          return false
        }

        if(!this.crud.manual.date) {
          this.$awn.alert('No se cargó la fecha')
          return false
        }

        if(!this.crud.manual.reference) {
          this.$awn.alert('No se cargó la referencia')
          return false
        }

        if(!this.crud.manual.detail) {
          this.$awn.alert('Revise el asiento contable')
          return false
        }

        let loader = this.$loading.show()
        let result = this.crud.manual.id ? serviceAPI.editarAsiento(this.crud.manual) : serviceAPI.agregarAsiento(this.crud.manual)

        result
        .then(() => {
          loader.hide()
          this.modal.manual.active = false
          this.filterReportReset()
          this.$awn.success('Asiento manual guardado con éxito')
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: options.icon || (danger ? 'warning' : 'help') } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      revertApproved(item) {
        this.confirmDialog("¿Desea borrar el asiento de '" + item.reference + "'? El comprobante vuelve a Asientos Pendientes y gestión puede volver a modificarlo.", {
          title: 'Borrar asiento',
          icon: 'undo',
          danger: true,
          okTitle: 'Borrar',
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.borrarAsientoAprobado(item.id)
            .then(() => {
              loader.hide()
              this.$awn.success('Asiento borrado')
              this.filterReport()
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      deleteManualAccounting(item) {
        this.confirmDialog("¿Desea eliminar el asiento manual '"+ item.reference +"'?", {
          title: 'Eliminar asiento manual',
          icon: 'delete',
          danger: true,
          okTitle: 'Eliminar',
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.eliminarAsiento(item.id)
            .then(() => {
              loader.hide()
              this.filterReportReset()
              this.$awn.success('Asiento manual eliminado')
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      filterReport(){
        let loader = this.$loading.show()

        var result = serviceAPI.filtrarAsientos(this.filters, this.table.currentPage)

        result.then((response) => {
          loader.hide()
          this.table.items = response.data.data || []
          this.table.tablePaginate = response.data

          this.arr.accounting = response.data.data || []

          this.tableSub.items = []
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        });
      },
    }
  }
</script>
<style scoped>
  ::v-deep .accounting-entry-row {
    cursor: pointer;
  }
  .filter-find-object ::v-deep fieldset {
    margin-bottom: 0 !important;
  }

  .accounting-entries-amount {
    font-size: 13px;
  }

  .filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .nd-marker {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #fd7e14;
    cursor: default;
  }
</style>
